import { Component, ReactNode, ErrorInfo } from 'react';
import { logger } from '../services/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  maxRetries?: number;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
  isRetrying: boolean;
}

// SVG Icons
const AlertTriangleIcon = () => (
  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
    <line x1="12" y1="9" x2="12" y2="13"></line>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
);

const RefreshIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"></polyline>
    <polyline points="1 20 1 14 7 14"></polyline>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
  </svg>
);

const HomeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    <polyline points="9 22 9 12 15 12 15 22"></polyline>
  </svg>
);

/**
 * ErrorBoundary Component
 * Capture les erreurs React et affiche un fallback UI avec retry automatique
 */
export class ErrorBoundary extends Component<Props, State> {
  private retryTimeoutId?: number;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false
    };
  }

  static getDerivedStateFromError(_error: Error): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // ✅ Check for chunk load errors (deployment/cache issue)
    const isChunkLoadError =
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Loading CSS chunk') ||
      error.name === 'ChunkLoadError';

    if (isChunkLoadError) {
      // Log the error
      logger.warn('ErrorBoundary', 'Chunk load error detected, reloading page...', {
        error: error.message
      });

      // Show a brief message and reload
      const reloadKey = 'chunk_reload_attempted';
      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();

      // Prevent infinite reload loop (only reload once per 30 seconds)
      if (!lastReload || (now - parseInt(lastReload)) > 30000) {
        sessionStorage.setItem(reloadKey, now.toString());
        // Force reload to get fresh assets
        window.location.reload();
        return;
      }
    }

    // ✅ Log to logger service (Issue #5 fix)
    logger.error('ErrorBoundary', 'Component crash detected', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });

    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Call external error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Also log to console in development
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  componentWillUnmount(): void {
    if (this.retryTimeoutId) {
      window.clearTimeout(this.retryTimeoutId);
    }
  }

  handleRetry = (): void => {
    const { maxRetries = 3 } = this.props;
    const { retryCount } = this.state;

    if (retryCount >= maxRetries) {
      return;
    }

    this.setState({ isRetrying: true });

    // Exponential backoff: 1s, 2s, 4s
    const delay = Math.pow(2, retryCount) * 1000;

    this.retryTimeoutId = window.setTimeout(() => {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prevState.retryCount + 1,
        isRetrying: false
      }));
    }, delay);
  };

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false
    });
  };

  render(): ReactNode {
    const { maxRetries = 3 } = this.props;
    const { hasError, error, errorInfo, retryCount, isRetrying } = this.state;

    if (hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="error-boundary-container">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">
              <AlertTriangleIcon />
            </div>

            <h2 className="error-boundary-title">Une erreur s'est produite</h2>
            <p className="error-boundary-message">
              Nous sommes désolés, une erreur inattendue s'est produite. Vos données sont sauvegardées.
            </p>

            {retryCount < maxRetries && (
              <div className="error-boundary-retry-info">
                <p>Tentatives de récupération : {retryCount} / {maxRetries}</p>
                {isRetrying && (
                  <div className="error-boundary-progress">
                    <div className="error-boundary-progress-bar"></div>
                  </div>
                )}
              </div>
            )}

            {error && (
              <details className="error-boundary-details">
                <summary className="error-boundary-details-summary">
                  Détails de l'erreur
                </summary>
                <pre className="error-boundary-details-pre">
                  <strong>Error:</strong> {error.toString()}
                  {errorInfo && (
                    <>
                      {'\n\n'}
                      <strong>Component Stack:</strong>
                      {errorInfo.componentStack}
                    </>
                  )}
                </pre>
              </details>
            )}

            <div className="error-boundary-actions">
              <button
                onClick={this.handleRetry}
                disabled={retryCount >= maxRetries || isRetrying}
                className="error-boundary-btn error-boundary-btn-primary"
              >
                <RefreshIcon />
                {isRetrying ? 'Réessai en cours...' : 'Réessayer'}
              </button>
              <button
                onClick={() => window.location.reload()}
                className="error-boundary-btn error-boundary-btn-secondary"
              >
                <HomeIcon />
                Recharger la page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
